import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReenviarVerificacion from '../components/ReenviarVerificacion';
import PasswordInput from '../components/PasswordInput';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailNoVerificado, setEmailNoVerificado] = useState('');
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('session') === 'displaced') {
      toast.warning('Tu sesión fue cerrada porque iniciaste sesión desde otro dispositivo');
    }
  }, []);

  // Redirigir al dashboard si ya está autenticado
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        toast.success('¡Bienvenido!');
        navigate('/');
      } else if (result.error === 'EMAIL_NO_VERIFICADO') {
        setEmailNoVerificado(email);
      } else {
        toast.error('❌ Usuario o contraseña incorrectos');
      }
    } catch (error) {
      toast.error('❌ Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* 🎨 DECORACIÓN MODERNA CON PRIMARY */}
      
      {/* Líneas diagonales con blur */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `repeating-linear-gradient(
          45deg,
          rgba(59, 130, 246, 0.02) 0px,
          rgba(59, 130, 246, 0.02) 20px,
          transparent 20px,
          transparent 40px
        )`,
        pointerEvents: 'none'
      }} />

      {/* Círculos grandes con blur (primary) */}
      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-100px',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        pointerEvents: 'none'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        right: '-100px',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />

      {/* Círculos pequeños flotantes */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '10%',
        width: '100px',
        height: '100px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(20px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        bottom: '30%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(30px)',
        pointerEvents: 'none'
      }} />

      {/* Header con estilo glassmorphism */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        padding: '0.75rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.05)',
        zIndex: 1000
      }}>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#3b82f6',
          textShadow: '0 2px 4px rgba(59, 130, 246, 0.2)'
        }}>
          <span>🎯</span> FactuTracks
        </div>
        
        {/* Botón Volver estilo btn-secondary */}
        <a 
          href="https://factutracks.com" 
          style={{
            backgroundColor: 'white',
            color: '#4b5563',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1.25rem',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            fontWeight: '500',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#93c5fd';
            e.target.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.color = '#4b5563';
          }}
        >
          <span>←</span> Volver
        </a>
      </div>

      {/* Card de login con decoración y vidrio esmerilado */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderRadius: '32px',
        boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.5)',
        width: '100%',
        maxWidth: '440px',
        padding: '3rem',
        marginTop: '80px',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10
      }}>
        {/* Borde con gradiente */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6)',
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px'
        }} />

        {/* Decoración interna */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <h2 style={{
          fontSize: '2rem',
          fontWeight: '800',
          color: '#111827',
          textAlign: 'center',
          marginBottom: '0.5rem',
          position: 'relative'
        }}>
          Bienvenido
        </h2>
        <p style={{
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '0.875rem',
          marginBottom: '2.5rem',
          position: 'relative'
        }}>
          Inicia sesión para continuar
        </p>

        <form onSubmit={handleSubmit} style={{ position: 'relative' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: '600',
              color: '#374151',
              fontSize: '0.875rem'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.875rem 1.25rem',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                outline: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="tu@email.com"
            />
          </div>

          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <label style={{ fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Contraseña
              </label>
              <Link to="/forgot-password" style={{ fontSize: '0.8rem', color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                width: '100%',
                padding: '0.875rem 1.25rem',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                outline: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
              placeholder="••••••••"
            />
          </div>

          {/* Botón primary con efecto shine */}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.875rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 8px 14px -4px rgba(59, 130, 246, 0.4)',
              marginBottom: '1rem',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#2563eb';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 12px 20px -6px rgba(59, 130, 246, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = '#3b82f6';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 8px 14px -4px rgba(59, 130, 246, 0.4)';
              }
            }}
          >
            {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
          </button>
        </form>

        {emailNoVerificado && (
          <div style={{
            marginTop: '1.25rem',
            padding: '1rem',
            backgroundColor: '#fffbeb',
            border: '1px solid #fbbf24',
            borderRadius: '12px',
            fontSize: '0.875rem',
            color: '#92400e',
            textAlign: 'center',
          }}>
            <div style={{ fontWeight: '700', marginBottom: '0.4rem' }}>⚠️ Correo no verificado</div>
            <p style={{ margin: '0 0 0.5rem', color: '#78350f' }}>
              Debes confirmar tu correo antes de iniciar sesión.
            </p>
            <p style={{ margin: 0, color: '#6b7280' }}>
              ¿No recibiste el correo?{' '}
              <ReenviarVerificacion email={emailNoVerificado} />
            </p>
          </div>
        )}

        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          ¿No tienes cuenta?{' '}
          <Link
            to="/register"
            style={{
              color: '#3b82f6',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.color = '#2563eb'}
            onMouseLeave={(e) => e.target.style.color = '#3b82f6'}
          >
            Regístrate aquí
          </Link>
        </div>

        {/* Demo info con estilo */}
        <div style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: 'rgba(59, 130, 246, 0.05)',
          borderRadius: '16px',
          fontSize: '0.875rem',
          color: '#4b5563',
          textAlign: 'center',
          border: '1px solid rgba(59, 130, 246, 0.1)'
        }}>
          {/* <strong style={{ color: '#3b82f6' }}>Demo:</strong> admin@peluqueria.com / admin123 */}
        </div>
      </div>
    </div>
  );
};

export default Login;